import {Component, DestroyRef, inject, OnInit} from "@angular/core";
import {
  Aangifte,
  AangifteRapport,
  BokkzIbDataService,
  IbRekenService,
  IncomeTaxEntity,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {BehaviorSubject, filter, Observable, shareReplay, take} from "rxjs";
import {FormControl, FormGroup} from "@angular/forms";
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-box1-belastbaar-loon',
  templateUrl: './box1-belastbaar-loon.component.html',
  styleUrls: ['./box1-belastbaar-loon.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    CommonModule,
    SharedUiDetailsModule,
    SharedUiAlertModule
  ]
})
export class Box1BelastbaarLoonComponent implements OnInit {

  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private bokkzClient = inject(IbRekenService);
  private rapportRequestId = 0;
  public dataVisible$ = new BehaviorSubject<boolean>(false);
  public isDefAangifte = false;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public formGroup = new FormGroup({
    belastbaarloon: new FormControl<number | null>(0),
    loonUitTegenwoordigeDienstbetrekking: new FormControl<number | null>(0),
    loonUitTegenwoordigeDienstbetrekkingBuitenland: new FormControl<number | null>(0),
    loonUitTegenwoordigeDienstbetrekkingInternationaleOrganisatie: new FormControl<number | null>(0),
    loonUitVroegereDienstbetrekking: new FormControl<number | null>(0),
    loonUitVroegereDienstbetrekkingBuitenland: new FormControl<number | null>(0),
    loonUitVroegereDienstbetrekkingInternationaleOrganisatie: new FormControl<number | null>(0),
    reisaftrekOpenbaarVervoer: new FormControl<number | null>(0),
    vergoedingReisaftrek: new FormControl<number | null>(0),
    totaalToegepasteArbeidskorting: new FormControl<number | null>(0),
  });

  ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.isDefAangifte = data.taxType === 'DA';
    })
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((isPartner) => {
      const aangifte = this.dataService.incomeTaxEntity.value.aangifte;
      if (!aangifte) {
        this.dataVisible$.next(false);
        return;
      }
      this.getRapportIfChanged(aangifte, isPartner);
    });
  }

  public getRapportIfChanged(aangifte: Aangifte, partner : boolean) {
    const requestId = ++this.rapportRequestId;
    this.dataVisible$.next(false);

    if (partner) {
      this.bokkzClient.getIncomeTaxReport(aangifte.partnerAangifte!).subscribe(rapport => {
        if (requestId !== this.rapportRequestId) {
          return;
        }
        this.resetAndPatchForms(rapport);
      })
    }
    else {
      this.bokkzClient.getIncomeTaxReport(aangifte).subscribe(rapport => {
        if (requestId !== this.rapportRequestId) {
          return;
        }
        this.resetAndPatchForms(rapport);
      });
    }
  }

  private resetAndPatchForms(details: AangifteRapport): void {
    this.formGroup.reset({}, {emitEvent: false});
    this.formGroup.patchValue(details.specificatieLoon?.opbouw ?? {}, {emitEvent: false});
    this.formGroup.disable({emitEvent: false});
    this.dataVisible$.next(true);
  }

  public calculateBelastbaarloon(): any {
      const v = this.formGroup.value;
      const sum =
          (v.loonUitTegenwoordigeDienstbetrekking ?? 0) +
          (v.loonUitTegenwoordigeDienstbetrekkingBuitenland ?? 0) +
          (v.loonUitTegenwoordigeDienstbetrekkingInternationaleOrganisatie ?? 0) +
          (v.loonUitVroegereDienstbetrekking ?? 0) +
          (v.loonUitVroegereDienstbetrekkingBuitenland ?? 0) +
          (v.loonUitVroegereDienstbetrekkingInternationaleOrganisatie ?? 0) -
          (v.reisaftrekOpenbaarVervoer ?? 0) +
          (v.vergoedingReisaftrek ?? 0);

      this.formGroup.patchValue({ belastbaarloon: sum }, { emitEvent: false });
      return sum;
  }
}
